'use client'

import React from 'react';

/**
 * RefundPolicy Component
 * Features a high-contrast layout for legal clarity
 */
export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-white text-gray-700 selection:bg-red-500/20">
      
      {/* Background Glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-5%] left-[-5%] w-[40%] h-[40%] rounded-full bg-red-900/10 blur-[120px]" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[30%] h-[30%] rounded-full bg-indigo-900/10 blur-[100px]" />
      </div>

      <main className="relative z-10 w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16">
        
        {/* Header */}
        <header className="mb-12 border-b border-gray-100 pb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-red-500/30 bg-red-500/10 text-red-600 text-xs font-bold uppercase tracking-widest">
            Policy Update: No Refunds
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tighter mb-4">
            Refund & Cancellation
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl">
            Transparency is our priority. Please review our policy regarding payments, 
            cancellations, and service transfers before subscribing.
          </p>
        </header>

        {/* Main Card */}
        <div className="w-full bg-white border border-gray-200 rounded-[2.5rem] overflow-hidden shadow-sm">
          
          {/* High Alert Banner */}
          <div className="bg-gradient-to-r from-red-600/20 to-transparent p-6 md:p-8 border-b border-gray-100">
             <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20 text-red-500 text-sm">!</span>
                Strict No-Refund Policy
             </h2>
          </div>

          <div className="p-6 md:p-12 space-y-8 text-gray-700 leading-relaxed text-base md:text-lg">
            
            <section className="space-y-4">
              <p>
                Option Bricks takes all the necessary measures related to risk and rewards involved in 
                markets before delivering any recommendations to clients. 
                <span className="text-gray-900 font-semibold"> Once the client has paid for the services, there will be NO REFUNDS, CANCELLATIONS, or TRANSFERS.</span>
              </p>
              <p className="bg-gray-50 p-6 rounded-2xl border border-gray-100 text-sm italic">
                We appeal to our visitors not to allow anyone else, including family members or friends, 
                to use your electronic payment modes or personal accounts for subscriptions without 
                your prior permission.
              </p>
            </section>

            <section className="space-y-4">
              <p>
                By making a payment for a subscription to our site or bank account, we believe that you 
                have read and agreed to the <span className="text-indigo-400 font-bold">Option Bricks Refund Policy</span>. 
                Under no circumstances are payments reversible once service has been initiated.
              </p>
            </section>

            {/* Duplicate Transaction Rule */}
            <section className="p-6 md:p-8 rounded-3xl bg-indigo-500/5 border border-indigo-500/20">
              <h3 className="text-indigo-700 font-bold mb-3 uppercase text-sm tracking-widest">Duplicate Transactions</h3>
              <p className="text-sm md:text-base">
                If a client pays twice for a single transaction, the surplus amount will be refunded 
                via the <span className="text-gray-900">original payment source within 7 to 10 working days</span>. 
                This applies only if the client provides written intimation on the same day notifying 
                the concern.
              </p>
            </section>

            <section className="pt-6 border-t border-gray-100">
              <h3 className="text-gray-900 font-bold mb-6">Before making a payment, we strongly recommend:</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Read all information about our support services.",
                  "Review our full Terms and Conditions.",
                  "Examine our Privacy Policy thoroughly.",
                  "Acknowledge that no refund is possible in any case."
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="text-indigo-500 mt-1">✦</span>
                    <span className="text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Footer Call to Action */}
          <div className="bg-gray-50 p-8 text-center border-t border-gray-100">
            <p className="text-gray-600 text-sm">
              Kindly make the payment only after reading all 
              <span className="text-gray-900 font-bold mx-1 uppercase">terms and conditions, disclaimers, and refund policies</span>.
            </p>
          </div>
        </div>

        
      </main>
    </div>
  );
}